import { Box, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import React, { Fragment } from 'react'
import { CopyBlock, atomOneDark } from 'react-code-blocks'

const ReactNativeSDKLatestVersion = () => {
    return (
        <Box sx={{
            flex: '1 1 auto',
            minWidth: '200px',
            p: 2,
            bgcolor: 'rgba(11, 52, 82, 0.05)',
            borderRadius: '4px',
            border: '1px solid rgba(11, 52, 82, 0.1)'
        }}>
            <Typography sx={{ color: '#0b3452', fontWeight: 'bold', mb: 1 }}>
                React Native SDK
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Latest Version: <code>0.2.1</code>
            </Typography>
        </Box>
    )
}


const ReactNativeSDKPrerequisites = () => {
    return (
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
            <li>
                <Typography sx={{ lineHeight: 1.7 }}>
                    Node version ≥ 16.x
                </Typography>
            </li>
            <li>
                <Typography sx={{ lineHeight: 1.7 }}>
                    React Native Version ≥ 0.72.0
                </Typography>
            </li>
            <li>
                <Typography sx={{ lineHeight: 1.7 }}>
                    Android : Minimum SDK version 25
                </Typography>
                <Typography sx={{ lineHeight: 1.7 }}>
                    Android : target SDK version 35
                </Typography>
            </li>
            <li>
                <Typography sx={{ lineHeight: 1.7 }}>
                    iOS: Minimum iOS version 16
                </Typography>
            </li>
        </Box>
    )
}

const ReactNativeSDKInstallation = () => {
    return (
        <>
            <Typography sx={{ mb: 2 }}>
                1. Prebuild your Expo app (if not already done). This step is necessary to generate the native `android` and `ios` directories so you can apply manual changes required by native SDKs.
            </Typography>
            <CopyBlock
                text={`#Run this command in your project directory
npx expo prebuild`}
                language="bash"
                showLineNumbers={false}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 2, mb: 2 }}>
                For more details on prebuilding, see the{' '}
                <a href="https://docs.expo.dev/workflow/continuous-native-generation/" target="_blank" rel="noopener noreferrer">
                    official Expo prebuild documentation
                </a>
            </Typography>

            <Typography sx={{ mt: 4, mb: 2 }}>
                2. Authenticate with GitHub npm registry:
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                You'll need to create a <a href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>GitHub Personal Access Token (PAT)</a> with <code>read:packages</code> permission. 
                Run the following command and enter your GitHub username and token when prompted:
            </Typography>
            <CopyBlock
                text={`npm login --scope=@cookiejar-technologies --registry=https://npm.pkg.github.com`}
                language="bash"
                showLineNumbers={false}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 4, mb: 2 }}>
                3. Install the latest finvu-react-native-sdk:
            </Typography>
            <CopyBlock
                text={`npm install @cookiejar-technologies/finvu-react-native-sdk`}
                language="bash"
                showLineNumbers={false}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 4, mb: 2 }}>
                4. Add the following Maven repository to your <code>android/build.gradle</code>. GitHub packages require credentials for access. While our repository is public and can be accessed with any GitHub user, you'll need to create a <a href="https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>Personal Access Token (PAT)</a> to use as GITHUB_PACKAGE_TOKEN:
            </Typography>
            <CopyBlock
                text={`allprojects {
    repositories {
        google()
        mavenCentral()

        // Add this block
        maven { 
            url 'https://maven.pkg.github.com/Cookiejar-technologies/finvu_android_sdk'
            credentials {
                username = System.getenv("GITHUB_PACKAGE_USERNAME")
                password = System.getenv("GITHUB_PACKAGE_TOKEN")
            }
        }
    }
}`}
                language="groovy"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 3, mb: 2 }}>
                5. In <code>android/app/build.gradle</code>, update your <code>defaultConfig</code>:
            </Typography>
            <CopyBlock
                text={`defaultConfig {
    minSdkVersion 25
    targetSdkVersion 35
    ...
}`}
                language="groovy"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 3, mb: 2 }}>
                6. In your <code>ios/Podfile</code>, configure the SDK as follows:
            </Typography>
            <CopyBlock
                text={`platform :ios, '16.0'
#check the latest sdk version in the Latest SDK Versions section
pod 'FinvuSDK', :git => 'https://github.com/Cookiejar-technologies/finvu_ios_sdk.git', :tag => '1.1.1'`}
                language="ruby"
                showLineNumbers={true}
                theme={atomOneDark}
            />
        </>
    )
}

const ReactNativeSDKInitialisationAndConnectionManagement: React.FC<{
    sslCertificatePinningInfoComponent: JSX.Element
}> = ({ sslCertificatePinningInfoComponent }) => {
    return (
        <>
            <Typography sx={{ mb: 2 }}>
                Import the SDK module:
            </Typography>
            <CopyBlock
                text={`import * as Finvu from 'finvu-react-native-sdk';`}
                language="javascript"
                showLineNumbers={false}
                theme={atomOneDark}
            />

            {/* 1. Initialize */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                1. Initialize with config:
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Initialize the SDK in your app's entry point (e.g., splash screen).
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                URLs: UAT: <code>"wss://webvwdev.finvu.in/consentapiv2"</code>, PROD: <code>"wss://wsslive.finvu.in/consentapiv2"</code>
            </Typography>
            <CopyBlock
                text={`const config = {
  finvuEndpoint: 'wss://wsslive.finvu.in/consentapiv2',
  certificatePins: [
    'TmZriS3UEzT3t5s8SJATgFdUH/llYL8vieP2wOuBAB8=', // Pin #1
    'aBcDeFgHiJkLmNoPqRsTuVwXyZ1234567890+/='       // Pin #2
  ]
};

const response = await Finvu.initializeWith(config);

if (response.isSuccess) {
  console.log('Initialized:', response.data); // ✅ string
} else {
  console.error('Initialization failed:', response.error.message);
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 1, mb: 1, color: '#555' }}>
                ✅ <strong>Response Type:</strong> <code>Result&lt;string&gt;</code>
            </Typography>
            <Typography sx={{ ml: 2, mb: 3, fontFamily: 'monospace', color: '#999' }}>
                Success: {"{ isSuccess: true, data: 'Initialized successfully' }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Initialization failed' } }"}
            </Typography>

            {sslCertificatePinningInfoComponent}

            {/* 2. Connect */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                2. Connect:
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                Call this before making any other requests. Connects to the Finvu AA server using WebSocket.
            </Typography>
            <CopyBlock
                text={`const response = await Finvu.connect();

if (response.isSuccess) {
  console.log('Connected successfully');
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  switch (errorCode) {
    case '8000':
      // Session disconnected - retry connection
      await new Promise(resolve => setTimeout(resolve, 2000));
      await Finvu.connect();
      break;
    case '8001':
      // SSL pinning failure
      console.error('SSL certificate validation failed');
      break;
    case '9999':
      // Generic error
      console.error('Connection failed:', errorMessage);
      break;
    default:
      console.error('Connection failed:', errorMessage);
  }
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 1, mb: 1, color: '#555' }}>
                ✅ <strong>Response Type:</strong> <code>Result&lt;void&gt;</code>
            </Typography>
            <Typography sx={{ ml: 2, mb: 3, fontFamily: 'monospace', color: '#999' }}>
                Success: {"{ isSuccess: true, data: undefined }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Connection failed' } }"}
            </Typography>

            {/* 3. Disconnect */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                3. Disconnect:
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                Gracefully disconnects from the Finvu AA server.
            </Typography>
            <CopyBlock
                text={`const response = await Finvu.disconnect();

if (response.isSuccess) {
  console.log('Disconnected successfully');
} else {
  console.error('Disconnection failed:', response.error.message);
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 1, mb: 1, color: '#555' }}>
                ✅ <strong>Response Type:</strong> <code>Result&lt;void&gt;</code>
            </Typography>
            <Typography sx={{ ml: 2, mb: 3, fontFamily: 'monospace', color: '#999' }}>
                Success: {"{ isSuccess: true, data: undefined }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Failed to disconnect from finvu' } }"}
            </Typography>
        </>
    );
};

const ReactNativeSDKAuthentication = () => {
    return (
        <>
            {/* Login */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                1. Login with Username or Mobile Number
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Initiates login using either a username (email format) or mobile number. Sends an OTP to the registered number. Stores a reference used for OTP verification.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>username</code>: <i>string</i> — User’s username (email format)</li>
                <li><code>mobileNumber</code>: <i>string</i> — User’s mobile number</li>
                <li><code>consentHandleId</code>: <i>string</i> — Consent handle ID for the session</li>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&#x3C;{'{ reference: string }'}&#x3E;</code>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { reference: 'abc123' } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Login failed' } }"}
            </Typography>
            <CopyBlock
                text={`const response = await Finvu.loginWithUsernameOrMobileNumber(
  '9999999999@finvu',
  '9999999999',
  'CONSENT_HANDLE_ID'
);

if (response.isSuccess) {
  const otpReference = response.data.reference;
  // Store otpReference for OTP verification
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  switch (errorCode) {
    case 'A001':
      console.error('Invalid login user format');
      break;
    case 'A003':
      console.error('OTP initiation failed');
      break;
    case 'F401':
      console.error('Unauthorized');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    case 'F429':
      console.error('Max attempts exceeded');
      break;
    case '1001':
      console.error('Login retry in progress');
      break;
    case '1002':
      console.error('Login failed permanently');
      break;
    default:
      console.error('Login failed:', errorMessage);
  }
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Verify OTP */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                2. Verify Login OTP
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Verifies the OTP sent to the user’s phone and authenticates the session.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>otp</code>: <i>string</i> — OTP received via SMS</li>
                <li><code>otpReference</code>: <i>string</i> — OTP reference from the loginWithUsernameOrMobileNumber response</li>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&#x3C;{'{ userId: string }'}&#x3E;</code>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { userId: 'finvu-user-id' } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'OTP verification failed' } }"}
            </Typography>
            <CopyBlock
                text={`const response = await Finvu.verifyLoginOtp(
  '123456',
  otpReference
);

if (response.isSuccess) {
  const userId = response.data.userId;
  // Proceed with authenticated flow
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  switch (errorCode) {
    case 'A004':
      console.error('Login OTP verification failed');
      break;
    case 'A005':
      console.error('Invalid OTP');
      // Allow user to retry
      break;
    case 'A006':
      console.error('Invalid OTP Reference');
      // Restart login flow
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    case 'F429':
      console.error('Max attempts exceeded');
      break;
    default:
      console.error('OTP verification failed:', errorMessage);
  }
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 3, mb: 2 }}>
                🔁 To resend the OTP during login, call <code>loginWithUsernameOrMobileNumber</code> again with the same parameters.
            </Typography>

            {/* Logout */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                3. Logout
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Ends the session and disconnects from Finvu securely.
            </Typography>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&lt;void&gt;</code>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: undefined }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Logout failed' } }"}
            </Typography>
            <CopyBlock
                text={`const response = await Finvu.logout();

if (response.isSuccess) {
  console.log('Logged out successfully');
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  // Even if logout fails, disconnect to clean up
  await Finvu.disconnect();
  
  if (errorCode === 'F402' || errorCode === 'A002') {
    // Session already invalid - just disconnect
    console.log('Session already invalidated');
  } else {
    console.error('Logout failed:', errorMessage);
  }
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />
        </>
    );
};

const ReactNativeSDKFIPManagement = () => {
    return (
        <>
            {/* 1. Fetch All FIP Options */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                1. Fetch All FIP Options
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                Retrieves a list of all available Financial Information Providers (FIPs). Each FIP includes FiTypes and optional metadata like name, icon, and status.
            </Typography>

            <Typography sx={{ fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <Typography sx={{ mb: 1, ml: 2, fontStyle: 'italic' }}>
                This method takes no parameters.
            </Typography>

            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&#x3C;FipsAllFIPOptionsResponse&#x3E;</code>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { searchOptions: FIPInfo[] } }"}<br />
                Error: {"{ isSuccess: false, error: { message: 'Fetching FIPs failed' } }"}
            </Typography>

            <Typography sx={{ mt: 2 }}>
                Each <code>FIPInfo</code> object includes:
            </Typography>
            <ul>
                <li><code>fipId</code>: <i>string</i></li>
                <li><code>productName</code>: <i>string (optional)</i></li>
                <li><code>fipFiTypes</code>: <i>string[]</i> — List of FI Types</li>
                <li><code>productDesc</code>: <i>string (optional)</i></li>
                <li><code>productIconUri</code>: <i>string (optional)</i></li>
                <li><code>enabled</code>: <i>boolean</i></li>
            </ul>

            <CopyBlock
                text={`const response = await Finvu.fipsAllFIPOptions();

if (response.isSuccess) {
  const allFips = response.data.searchOptions;
  console.log(allFips);
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  switch (errorCode) {
    case 'F401':
      console.error('Unauthorized');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    case 'F500':
      console.error('Internal server error');
      break;
    default:
      console.error('Failed to fetch FIPs:', errorMessage);
  }
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* 2. Fetch FIP Details */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                2. Fetch FIP Details
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                Fetches detailed information for a specific FIP using the FIP ID obtained from <code>fipsAllFIPOptions</code>. Includes FI Type and the required identifier structure for each FI Type. The returned <code>FipDetails</code> may include <code>linkingOtpLength</code> (nullable) — when present, it indicates the length of the FIP linking OTP used for linking accounts (e.g. for OTP input UI).
            </Typography>

            <Typography sx={{ fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>fipId</code>: <i>string</i> — ID of the selected Financial Information Provider</li>
            </ul>

            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&#x3C;FipDetails&#x3E;</code>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { fipId, typeIdentifiers: [...], linkingOtpLength?: number | null } }"}<br />
                Error: {"{ isSuccess: false, error: { message: 'Fetching FIP details failed' } }"}
            </Typography>

            <Typography sx={{ mt: 2 }}>
                Each <code>FipDetails</code> object includes:
            </Typography>
            <ul>
                <li><code>fipId</code>: <i>string</i></li>
                <li><code>typeIdentifiers</code>: <i>FipFiTypeIdentifier[]</i></li>
                <li><code>linkingOtpLength</code>: <i>number | null | undefined</i> — optional; when present, the length of the FIP linking OTP for linking accounts</li>
            </ul>

            <Typography sx={{ mt: 2 }}>
                Each <code>FipFiTypeIdentifier</code> includes:
            </Typography>
            <ul>
                <li><code>fiType</code>: <i>string</i></li>
                <li><code>identifiers</code>: <i>TypeIdentifier[]</i></li>
            </ul>

            <Typography sx={{ mt: 2 }}>
                Each <code>TypeIdentifier</code> includes:
            </Typography>
            <ul>
                <li><code>category</code>: <i>string</i></li>
                <li><code>type</code>: <i>string</i></li>
            </ul>

            <CopyBlock
                text={`const response = await Finvu.fetchFipDetails('FINVU-FIP');

if (response.isSuccess) {
  const fipDetails = response.data;
  console.log(fipDetails.typeIdentifiers);
  const linkingOtpLength = fipDetails.linkingOtpLength;
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  switch (errorCode) {
    case 'F404':
      console.error('FIP details not found');
      break;
    case 'F406':
    case 'D007':
      console.error('Invalid FIP');
      break;
    case 'F407':
      console.error('FIP failure');
      break;
    case 'F401':
      console.error('Unauthorized');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Failed to fetch FIP details:', errorMessage);
  }
}`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />
        </>
    );
};

const ReactNativeSDKAccountDiscovery = () => {
    return (
        <>
            {/* 1. Discover Accounts (Synchronous) */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                1. Discover Accounts (Synchronous)
            </Typography>

            <Typography sx={{ mb: 2, color: "#555" }}>
                Executes a synchronous request to discover accounts for a given FIP using FI types and user identifiers like mobile number, PAN, or date of birth. This method returns masked account details grouped by financial institution.
            </Typography>

            <Typography sx={{ fontWeight: "bold" }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li>
                    <code>fipId</code>: <i>string</i> — ID of the Financial Information Provider
                </li>
                <li>
                    <code>fiTypes</code>: <i>string[]</i> — List of FI Types to search (e.g., <code>["DEPOSIT", "RECURRING_DEPOSIT"]</code>)
                </li>
                <li>
                    <code>identifiers</code>: <i>{'{ category: string; type: string; value: string }[]'}</i> — Identifiers used for account discovery
                </li>
            </ul>

            <Typography sx={{ fontWeight: "bold" }}>
                ✅ Response Type: <code>Result&#x3C;DiscoverAccountsResponse&#x3E;</code>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: "monospace", color: "#999", mb: 1 }}>
                Success: {"{ isSuccess: true, data: { discoveredAccounts: DiscoveredAccount[] } }"}
                <br />
                Error: {"{ isSuccess: false, error: { message: 'Discovery failed' } }"}
            </Typography>

            <CopyBlock
                text={`const response = await Finvu.discoverAccounts(
  "FINVU-FIP",
  ["DEPOSIT", "RECURRING_DEPOSIT"],
  [
    { category: "STRONG", type: "MOBILE", value: "9999999999" }
  ]
);

if (response.isSuccess) {
  const accounts = response.data.discoveredAccounts;
  console.log(accounts);
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  switch (errorCode) {
    case 'D001':
      console.error('User ID mismatch');
      // Re-login required
      break;
    case 'D002':
      console.error('Unsupported financial information type');
      break;
    case 'D003':
      console.error('Device not authenticated');
      break;
    case 'D004':
      console.error('Email not authenticated');
      break;
    case 'D005':
      console.error('Aadhar not authenticated');
      break;
    case 'D006':
      console.error('Authenticator required');
      break;
    case 'D007':
    case 'F406':
      console.error('Invalid FIP');
      break;
    case 'D008':
      console.error('FIP operation failed');
      // May need to retry or wait
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Discovery failed:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Response Structure */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                📦 Response Structure
            </Typography>

            <Typography sx={{ mt: 2 }}>
                Each <code>DiscoveredAccount</code> includes:
            </Typography>
            <ul>
                <li>
                    <code>accountReferenceNumber</code>: <i>string</i>
                </li>
                <li>
                    <code>accountType</code>: <i>string</i>
                </li>
                <li>
                    <code>fiType</code>: <i>string</i>
                </li>
                <li>
                    <code>maskedAccountNumber</code>: <i>string</i>
                </li>
            </ul>

            <CopyBlock
                text={`{
  discoveredAccounts: [
    {
      accountReferenceNumber: "XYZ123",
      accountType: "SAVINGS",
      fiType: "DEPOSIT",
      maskedAccountNumber: "XXXXXX1234"
    }
  ]
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Identifier Examples */}
            <Typography variant="h6" sx={{ mt: 4, color: "#0b3452", fontWeight: "bold" }}>
                Identifier Examples
            </Typography>

            <Typography sx={{ mt: 3, mb: 2 }}>
                1. <b>Banks</b> require Mobile Number as a <i>STRONG</i> identifier:
            </Typography>
            <CopyBlock
                text={`[
  { category: "STRONG", type: "MOBILE", value: "930910XXXX" }
]`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 3, mb: 2 }}>
                2. <b>Investments</b> require Mobile as <i>STRONG</i> and PAN as <i>WEAK</i> identifier:
            </Typography>
            <CopyBlock
                text={`[
  { category: "STRONG", type: "MOBILE", value: "930910XXXX" },
  { category: "WEAK", type: "PAN", value: "DFKPGXXXXR" }
]`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 3, mb: 2 }}>
                3. <b>Insurance</b> requires Mobile as <i>STRONG</i> and DOB as <i>ANCILLARY</i>:
            </Typography>
            <CopyBlock
                text={`[
  { category: "STRONG", type: "MOBILE", value: "930910XXXX" },
  { category: "ANCILLARY", type: "DOB", value: "yyyy-MM-dd" }
]`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />
        </>
    );
};

const ReactNativeSDKAccountLinking = () => {
    return (
        <>
            {/* 🔗 Link Accounts */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                1. Link Accounts
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Initiates account linking by passing selected discovered accounts and FIP details. Returns linked accounts and a reference number used for OTP confirmation.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>selectedAccountsToLink</code>: <i>DiscoveredAccount[]</i> — List of selected discovered accounts to link</li>
                <Typography sx={{ mb: 2 }}>
                    <CopyBlock text={`DiscoveredAccount: {
  accountReferenceNumber: string;
  accountType: string;
  fiType: string;
  maskedAccountNumber: string;
}`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </Typography>
                <li><code>fipDetails</code>: <i>FipDetails</i> — Object containing selected FIP ID and FI type identifiers</li>
                <Typography sx={{ mb: 2 }}>
                    <CopyBlock text={` FipDetails: {
  fipId: string;
  typeIdentifiers: FipFiTypeIdentifier[];
}
  FipFiTypeIdentifier: {
  fiType: string;
  identifiers: TypeIdentifier[];
}
  TypeIdentifier:{
  category: string;
  type: string;
}`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark} />
                </Typography>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&lt;{'{ referenceNumber?: string }'}&gt;</code>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { referenceNumber: 'abc123' } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Linking accounts failed' } }"}
            </Typography>
            <CopyBlock
                text={`const result = await Finvu.linkAccounts(selectedAccountsToLink, selectedFipDetails);

if (result.isSuccess) {
  const referenceNumber = result.data.referenceNumber;
  // Store referenceNumber for OTP confirmation
} else {
  const errorCode = result.error.code;
  const errorMessage = result.error.message;
  
  switch (errorCode) {
    case 'D009':
      console.error('Account already linked');
      // Fetch linked accounts and show to user
      const linkedAccountsResult = await Finvu.fetchLinkedAccounts();
      if (linkedAccountsResult.isSuccess) {
        const linkedAccounts = linkedAccountsResult.data.linkedAccounts;
        // Show existing linked accounts
      }
      break;
    case 'D011':
      console.error('Account reference number not found in linked accounts');
      break;
    case 'D007':
    case 'F406':
      console.error('Invalid FIP');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Linking failed:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* 🔐 Confirm Account Linking */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                2. Confirm Account Linking
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Confirms linking using OTP and reference number received from the <code>linkAccounts</code> call.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>referenceNumber</code>: <i>string</i> — Reference number from the linking initiation</li>
                <li><code>otp</code>: <i>string</i> — OTP received via SMS</li>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&lt;{'{ linkedAccounts: any[] }'}&gt;</code>
            </Typography>

            <Typography sx={{ mb: 2 }}>
                <ul>
                    <li>LinkedAccount: </li>
                    <CopyBlock text={`{
  customerAddress: string;
  accountReferenceNumber: string;
  linkReferenceNumber: string;
  status: string;
}`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </ul >
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { linkedAccounts: [...] } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'OTP verification failed' } }"}
            </Typography>
            <CopyBlock
                text={`const response = await Finvu.confirmAccountLinking(referenceNumber, "123456");

if (response.isSuccess) {
  const accounts = response.data.linkedAccounts;
  console.log('Account linking confirmed successfully');
} else {
  const errorCode = response.error.code;
  const errorMessage = response.error.message;
  
  switch (errorCode) {
    case 'A005':
      console.error('Invalid OTP');
      // Allow user to retry
      break;
    case 'A006':
      console.error('Invalid linking reference');
      // Restart linking process
      break;
    case 'D009':
      console.error('Account already linked');
      break;
    case 'D011':
      console.error('Invalid account reference');
      break;
    case 'F429':
      console.error('Max attempts exceeded');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Account linking confirmation failed:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            <Typography sx={{ mt: 3, mb: 2, color: '#555', fontStyle: 'italic' }}>
                🔁 To resend OTP during account linking, call <code>linkAccounts</code> again with the same parameters.
            </Typography>

            {/* 📥 Fetch Linked Accounts */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                3. Fetch Linked Accounts
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Retrieves a list of accounts linked for the currently authenticated user.
            </Typography>
            <Typography sx={{ fontWeight: 'bold', mt: 2 }}>
                ✅ Response Type: <code>Result&lt;{'{ linkedAccounts: any[] }'}&gt;</code>
            </Typography>
            <Typography sx={{ mb: 2 }}>
                <ul>
                    <li>FetchedLinkedAccount:</li>
                    <CopyBlock text={`{
  userId: string;
  fipId: string;
  fipName: string;
  maskedAccountNumber: string;
  accountReferenceNumber: string;
  linkReferenceNumber: string;
  consentIdList: string[];
  fiType: string;
  accountType: string;
  linkedAccountUpdateTimestamp: string;
  authenticatorType: string;
}`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </ul>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { linkedAccounts: [...] } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Failed to retrieve accounts' } }"}
            </Typography>
            <CopyBlock
                text={`const result = await Finvu.fetchLinkedAccounts();

if (result.isSuccess) {
  const accounts = result.data.linkedAccounts;
  console.log(\`Fetched \${accounts.length} linked accounts\`);
} else {
  const errorCode = result.error.code;
  const errorMessage = result.error.message;
  
  if (errorCode === 'D010') {
    // No linked accounts found - this is normal for new users
    console.log('No linked accounts found. User needs to discover and link accounts.');
    // Continue with discovery flow
  } else if (errorCode === 'F402' || errorCode === 'A002') {
    // Session error - reconnect
    await Finvu.disconnect();
    await Finvu.connect();
  } else {
    console.error('Failed to fetch linked accounts:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />
        </>
    );
};

const ReactNativeSDKConsentManagement = () => {
    return (
        <>
            {/* 🔗 Link Accounts */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                1. Get Consent Request Details
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Retrieves details of consent requests raised by FIU.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>consentHandleId</code>: <i>string</i></li>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&lt;{'{ consentDetail: consentDetail }'}&gt;</code>
            </Typography>
            <Typography sx={{ mb: 2 }}>
                <ul>
                    <li>consentDetail : </li>
                    <CopyBlock text={`{
  consentId: string | null;
  consentHandle: string;
  statusLastUpdateTimestamp: Date | null;
  financialInformationUser: FinancialInformationEntity;
  consentPurpose: ConsentPurpose;
  consentDisplayDescriptions: string[];
  dataDateTimeRange: DateTimeRange;
  consentDateTimeRange: DateTimeRange;
  consentDataLife: ConsentDataLifePeriod;
  consentDataFrequency: ConsentDataFrequency;
  fiTypes: string[] | null;
}

FinancialInformationEntity: {
  id: string;
  name: string;
}

ConsentPurpose: {
  code: string;
  text: string;
}

DateTimeRange: {
  from: Date;
  to: Date;
}

ConsentDataLifePeriod: {
  unit: string;
  value: number;
}

ConsentDataFrequency: {
  unit: string;
  value: number;
}`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </ul >
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { consentDetail: {...} } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Fetching consent request details failed' } }"}
            </Typography>
            <CopyBlock
                text={`const result = await Finvu.getConsentRequestDetails(consentHandleId);

if (result.isSuccess) {
  const consentDetail = result.data;
  console.log('Consent Handle:', consentDetail.consentHandle);
} else {
  const errorCode = result.error.code;
  const errorMessage = result.error.message;
  
  switch (errorCode) {
    case 'C003':
    case 'F404':
      console.error('Record not found');
      break;
    case 'C009':
      console.error('Consent handle not found');
      break;
    case 'C010':
      console.error('Consent handle required');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Failed to get consent details:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* 🔐 Confirm Account Linking */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                2. Approve Consent Request
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Approves consent request for selected accounts.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>consentDetails</code>: <i>ConsentDetail</i> — ConsentDetail optained from getConsentRequestDetails method</li>
                <li><code>selecteedFinvuLinkedAccounts</code>: <i>LinkedAccountDetails[]</i> — This is a list you get from fetchLinkedAccounts method , but do not directly send all linked accounts; Ask user for which accounts it wants to give consent.</li>
                <Typography sx={{ mb: 2 }}>
                    <CopyBlock text={` LinkedAccountDetails: {
  userId: string;
  fipId: string;
  fipName: string;
  maskedAccountNumber: string;
  accountReferenceNumber: string;
  linkReferenceNumber: string;
  consentIdList: string[] | null;
  fiType: string;
  accountType: string;
  linkedAccountUpdateTimestamp: Date | null;
  authenticatorType: string;
}`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark} />
                </Typography>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&lt;{'{ consentIntentId?: string; consentsInfo? : [...] }'}&gt;</code>
            </Typography>

            <Typography sx={{ mb: 2 }}>
                <ul>
                    <li>consentsInfo: </li>
                    <CopyBlock text={`{
   fipId?: string;
   consentId?: string;
}[]`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </ul >
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { consentIntentId?: string; consentsInfo? : [...] } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Approving consent failed' } }"}
            </Typography>
            <CopyBlock
                text={`const result = await Finvu.approveConsentRequest(
    consentDetailToSend,
    accountsToSend
);

if (result.isSuccess) {
  const consentIntentId = result.data.consentIntentId;
  const consentsInfo = result.data.consentsInfo;
  console.log('Consent approved successfully');
} else {
  const errorCode = result.error.code;
  const errorMessage = result.error.message;
  
  switch (errorCode) {
    case 'C001':
      console.error('Consent expired');
      // Fetch new consent request
      break;
    case 'C002':
      console.error('Consent already actioned');
      break;
    case 'C005':
      console.error('Invalid or malformed consent');
      break;
    case 'C006':
      console.error('User mismatch');
      break;
    case 'C007':
      console.error('FIU mismatch');
      break;
    case 'C008':
      console.error('Invalid linked account');
      break;
    case 'D010':
      console.error('No linked accounts found');
      break;
    case 'D011':
      console.error('Account reference not found in linked accounts');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Failed to approve consent:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* 📥 Fetch Linked Accounts */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                3. Deny Consent Request
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Denies consent request.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>consentRequestDetailInfo</code>: <i>ConsentDetail</i> — ConsentDetail optained from getConsentRequestDetails method</li>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&lt;{'{ consentIntentId?: string; consentsInfo? : [...] }'}&gt;</code>
            </Typography>

            <Typography sx={{ mb: 2 }}>
                <ul>
                    <li>consentsInfo: </li>
                    <CopyBlock text={`{
   fipId?: string;
   consentId?: string;
}[]`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </ul >
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { consentIntentId?: string; consentsInfo? : [...] } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Approving consent failed' } }"}
            </Typography>
            <CopyBlock
                text={`const result = await Finvu.denyConsentRequest(consentDetail);

if (result.isSuccess) {
  const consentIntentId = result.data.consentIntentId;
  const consentsInfo = result.data.consentsInfo;
  console.log('Consent denied successfully');
} else {
  const errorCode = result.error.code;
  const errorMessage = result.error.message;
  
  switch (errorCode) {
    case 'C001':
      console.error('Consent expired');
      break;
    case 'C002':
      console.error('Consent already actioned');
      break;
    case 'C004':
      console.error('Consent denied by user');
      break;
    case 'C005':
      console.error('Invalid or malformed consent');
      break;
    case 'F402':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Failed to deny consent:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* 📊 Get Consent Handle Status */}
            <Typography sx={{ mt: 3, mb: 2 }}>
                4. Get Consent Handle Status
            </Typography>
            <Typography sx={{ color: '#555' }}>
                Checks the status of a specific consent handle to determine if the consent is <code>ACCEPT</code>, <code>DENY</code>, or <code>PENDING</code>. Use this method after consent operations (approve or deny) to verify the final status and track consent state changes.
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: '#555', fontStyle: 'italic' }}>
                <strong>Note:</strong> This API can only be called a limited number of times (usually 5 times). Exceeding this limit will terminate the websocket session.
            </Typography>
            <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                📥 Parameters:
            </Typography>
            <ul>
                <li><code>handleId</code>: <i>string</i> — Consent handle ID</li>
            </ul>
            <Typography sx={{ fontWeight: 'bold' }}>
                ✅ Response Type: <code>Result&lt;ConsentHandleStatusResponse&gt;</code>
            </Typography>
            <Typography sx={{ mb: 2 }}>
                <ul>
                    <li>ConsentHandleStatusResponse:</li>
                    <CopyBlock text={`{
  status: ConsentHandleStatus  // "ACCEPT" | "DENY" | "PENDING"
}

// ConsentHandleStatus enum values:
// - ConsentHandleStatus.ACCEPT - Consent has been accepted
// - ConsentHandleStatus.DENY - Consent has been denied
// - ConsentHandleStatus.PENDING - Consent is still pending`}
                        language="tsx"
                        showLineNumbers={true}
                        theme={atomOneDark}
                    />
                </ul>
            </Typography>
            <Typography sx={{ ml: 2, fontFamily: 'monospace', color: '#999', mb: 1 }}>
                Success: {"{ isSuccess: true, data: { status: ConsentHandleStatus } }"}<br />
                Error: {"{ isSuccess: false, error: { code: 'ERROR_CODE', message: 'Failed to get consent status' } }"}
            </Typography>
            <CopyBlock
                text={`const result = await Finvu.getConsentHandleStatus("HANDLE_ID");

if (result.isSuccess) {
  const status = result.data.status;
  console.log('FinvuManager.getConsentHandleStatus - Success: Status =', status);
  
  switch (status) {
    case Finvu.ConsentHandleStatus.ACCEPT:
      // Consent has been accepted
      console.log('Consent has been accepted');
      // Process accepted consent
      break;
    case Finvu.ConsentHandleStatus.DENY:
      // Consent has been denied
      console.log('Consent has been denied');
      // Process denied consent
      break;
    case Finvu.ConsentHandleStatus.PENDING:
      // Consent is still pending
      console.log('Consent is still pending');
      // Handle pending status - may need to poll again later
      break;
    default:
      // Unknown status
      console.log('Unknown status:', status);
  }
} else {
  const errorCode = result.error.code;
  const errorMessage = result.error.message;
  
  switch (errorCode) {
    case 'C003':
    case 'F404':
      console.error('Record not found');
      break;
    case 'C009':
      console.error('Consent handle not found');
      break;
    case 'C010':
      console.error('Consent handle is required');
      break;
    case 'F429':
      console.error('Maximum polling attempts exceeded. Please check consent status later.');
      // Session may be terminated - reconnect if needed
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    case 'F402':
    case 'A002':
      // Session error - reconnect
      await Finvu.disconnect();
      await Finvu.connect();
      break;
    default:
      console.error('Failed to get consent status:', errorMessage);
  }
}`}
                language="tsx"
                showLineNumbers={true}
                theme={atomOneDark}
            />
        </>
    );
};

const ReactNativeSDKFAQSection = () => {

    const bulletPoints = [
        {
            primary: "Expo Go only supports JavaScript/TypeScript and built-in Expo SDK APIs.",
            secondary: "You cannot use native modules that require custom native code with the default Expo Go app."
        },
        {
            primary: "Native code must be compiled into the app.",
            secondary: "Modules like `finvu-react-native-sdk` rely on Android and iOS SDKs, so their native code must be bundled during build time."
        },
        {
            primary: "Expo Go cannot load native modules dynamically.",
            secondary: "If a native module isn’t part of the Expo Go binary, the app will throw runtime errors like `Cannot find native module 'Finvu'`."
        },
        {
            primary: "Solution: Use `expo prebuild` or EAS Build.",
            secondary: "These tools generate the necessary native code and compile it into your app so you can use custom native modules in production."
        }
    ];

    return (<Box sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#0b3452', mb: 1 }}>
            Q: Why do we need to prebuild an Expo app?
        </Typography>

        <Typography sx={{ mb: 2, color: '#555' }}>
            Prebuilding is necessary when your app relies on custom native code. Here's why:
        </Typography>

        <List sx={{ pl: 2 }}>
            {bulletPoints.map((point, index) => (
                <ListItem key={index} sx={{ alignItems: 'center', py: 0.5 }}>
                    <Typography sx={{ fontWeight: 'bold', mr: 1, mt: '1px', color: '#0b3452' }}>
                        {index + 1}.
                    </Typography>
                    <ListItemText
                        primary={point.primary}
                        secondary={point.secondary}
                        primaryTypographyProps={{ sx: { fontWeight: 500, fontSize: '0.95rem' } }}
                        secondaryTypographyProps={{ sx: { fontSize: '0.85rem', color: '#666' } }}
                    />
                </ListItem>
            ))}
        </List>
    </Box>
    )
}

const ReactNativeSDKScriptURLLoadingFAQ = () => {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#0b3452', mb: 1 }}>
                Q: Script URL Loading Issues
            </Typography>

            <Typography sx={{ mb: 2, color: '#555' }}>
                If you're experiencing issues with script URL loading, especially in production builds, follow these solutions:
            </Typography>

            {/* Metro Configuration Setup */}
            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold", mt: 3, mb: 1 }}>
                Metro Configuration Setup
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                First, ensure you have the correct Metro bundler configuration:
            </Typography>
            <CopyBlock
                text={`// metro.config.js
const { getDefaultConfig } = require('expo/metro-config');

const config = getDefaultConfig(__dirname);

// Add any custom configuration here if needed
// For example, to add additional asset extensions:
// config.resolver.assetExts.push('pem', 'key');

module.exports = config;`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 2, mb: 2, color: '#555' }}>
                This configuration uses Expo's default settings which handle most common cases.
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                If you're not using Expo, use this basic Metro config:
            </Typography>
            <CopyBlock
                text={`// metro.config.js
const { getDefaultConfig } = require('@react-native/metro-config');

module.exports = (async () => {
  const config = await getDefaultConfig(__dirname);
  
  // Add custom configurations here if needed
  return config;
})();`}
                language="javascript"
                showLineNumbers={true}
                theme={atomOneDark}
            />

            {/* Android Error */}
            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold", mt: 4, mb: 1 }}>
                Android: "Could not get script URL" Error
            </Typography>
            <Typography sx={{ mb: 1, color: '#555', fontWeight: 'bold' }}>
                Problem:
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                After building the production APK, the app fails to load with "Could not get script URL" error.
            </Typography>
            <Typography sx={{ mb: 1, color: '#555', fontWeight: 'bold' }}>
                Solution:
            </Typography>
            <CopyBlock
                text={`# Ensure the bundle exists in the correct location
mkdir -p android/app/src/main/assets

npx react-native bundle --platform android \\
  --dev false \\
  --entry-file index.ts \\
  --bundle-output android/app/src/main/assets/index.android.bundle \\
  --assets-dest android/app/src/main/res`}
                language="bash"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 2, mb: 2, color: '#555' }}>
                • Verify the bundle path in <code>android/app/src/main/AndroidManifest.xml</code><br />
                • Clean and rebuild: <code>cd android && ./gradlew clean && cd ..</code>
            </Typography>

            {/* iOS Error */}
            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold", mt: 4, mb: 1 }}>
                iOS: Bundle Loading Failed
            </Typography>
            <Typography sx={{ mb: 1, color: '#555', fontWeight: 'bold' }}>
                Problem:
            </Typography>
            <Typography sx={{ mb: 2, color: '#555' }}>
                Production IPA fails to load JavaScript bundle.
            </Typography>
            <Typography sx={{ mb: 1, color: '#555', fontWeight: 'bold' }}>
                Solution:
            </Typography>
            <CopyBlock
                text={`# Generate the iOS bundle
npx react-native bundle --platform ios \\
  --dev false \\
  --entry-file index.ts \\
  --bundle-output ios/main.jsbundle \\
  --assets-dest ios`}
                language="bash"
                showLineNumbers={true}
                theme={atomOneDark}
            />
            <Typography sx={{ mt: 2, mb: 2, color: '#555' }}>
                • Ensure bundle is included in Xcode project:<br />
                &nbsp;&nbsp;&nbsp;&nbsp;- Add <code>main.jsbundle</code> to project navigator in Xcode<br />
                &nbsp;&nbsp;&nbsp;&nbsp;- Check "Copy Bundle Resources" in Build Phases<br />
                • Verify bundle path in <code>Info.plist</code><br />
                • Clean build folder in Xcode: <strong>Product &gt; Clean Build Folder</strong>
            </Typography>
            <Typography sx={{ mt: 2, mb: 2, color: '#555', fontStyle: 'italic', bgcolor: 'rgba(11, 52, 82, 0.05)', p: 2, borderRadius: '4px' }}>
                <strong>Note:</strong> To run on iOS physical device and resolve bundle issues, the PC must be connected to the mobile phone's WhatsApp.
            </Typography>

            {/* Common Fixes */}
            <Typography variant="subtitle1" sx={{ color: "#0b3452", fontWeight: "bold", mt: 4, mb: 1 }}>
                Common Fixes for Both Platforms
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3, mb: 2 }}>
                <li>
                    <Typography sx={{ color: '#555' }}>
                        Verify entry file path matches your project structure (<code>index.js</code>/<code>index.ts</code>)
                    </Typography>
                </li>
                <li>
                    <Typography sx={{ color: '#555' }}>
                        Verify your <code>metro.config.js</code> is being picked up by checking the Metro logs at startup
                    </Typography>
                </li>
                <li>
                    <Typography sx={{ color: '#555' }}>
                        Check for any custom configurations needed in <code>metro.config.js</code> for your assets and dependencies
                    </Typography>
                </li>
            </Box>
        </Box>
    )
}


export {
    ReactNativeSDKLatestVersion,
    ReactNativeSDKPrerequisites,
    ReactNativeSDKInstallation,
    ReactNativeSDKInitialisationAndConnectionManagement,
    ReactNativeSDKAuthentication,
    ReactNativeSDKFIPManagement,
    ReactNativeSDKAccountDiscovery,
    ReactNativeSDKAccountLinking,
    ReactNativeSDKConsentManagement,
    ReactNativeSDKFAQSection,
    ReactNativeSDKScriptURLLoadingFAQ
}